import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, BadgeCheck, Award, Briefcase,
  HeartPulse, UserCheck, Microscope, Dumbbell, Stethoscope, ClipboardList,
  Building2, Search, Zap,
} from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import ShiftCard, { ShiftCardSkeleton } from "../../components/shifts/ShiftCard";
import HowItWorks from "../../components/shifts/HowItWorks";
import PageMeta from "../../components/seo/PageMeta";
import {
  SHIFT_CATEGORIES, HOMEPAGE_STATS, TRUST_PILLARS, HOW_IT_WORKS, SHIFTS_CTAS, MARKETPLACE_BRAND,
} from "../../data/marketplaceData";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

const ICONS = { HeartPulse, UserCheck, Microscope, Dumbbell, Stethoscope, ClipboardList, ShieldCheck, BadgeCheck, Award, Briefcase };

export default function ShiftsHome() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/shifts?limit=6`, { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error("Shift API returned " + r.status);
        return r.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Unexpected shift API payload");
        setShifts(data);
        setAvailabilityError(false);
      })
      .catch(() => { setShifts([]); setAvailabilityError(true); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ShiftsLayout>
      <PageMeta
        title="TRITAL ShiftBridge™ | Explore PRN Nursing, CNA & Allied Shifts"
        description="Pick up PRN shifts at vetted hospitals & SNFs nationwide. W-2 weekly pay, transparent rates, zero clinician fees. RN, LPN, CNA, ICU, ER, Med-Surg, L&D, Therapy & Allied PRN. Powered by TRITAL Care®."
        canonical={typeof window !== "undefined" && window.location.hostname === "shifts.tritalcare.com" ? "https://shifts.tritalcare.com/" : "https://www.tritalcare.com/shiftbridge"}
        ogImage="https://www.tritalcare.com/images/logos/trital-shiftbridge-logo-transparent-master.png"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": ["EmploymentAgency", "MedicalBusiness"],
            "name": "TRITAL ShiftBridge™",
            "alternateName": ["ShiftBridge", "TRITAL ShiftBridge", "ShiftBridge by TRITAL Care"],
            "url": "https://www.tritalcare.com/shiftbridge",
            "logo": "https://www.tritalcare.com/images/logos/trital-shiftbridge-logo-transparent-master.png",
            "image": "https://www.tritalcare.com/images/logos/trital-shiftbridge-logo-transparent-master.png",
            "description": "W-2 PRN shift marketplace built and operated by TRITAL Care®. Open shifts at vetted hospitals and SNFs nationwide. Weekly pay, transparent rates, zero clinician fees.",
            "areaServed": { "@type": "Country", "name": "United States" },
            "telephone": "+1-832-344-1525",
            "email": "shifts@tritalcare.com",
            "priceRange": "Free for clinicians",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "11104 West Airport Blvd, Suite 213",
              "addressLocality": "Stafford",
              "addressRegion": "TX",
              "postalCode": "77477",
              "addressCountry": "US"
            },
            "service": [
              { "@type": "Service", "name": "PRN Nursing Shifts" },
              { "@type": "Service", "name": "Per Diem RN Jobs" },
              { "@type": "Service", "name": "PRN LPN Jobs" },
              { "@type": "Service", "name": "PRN CNA Jobs" },
              { "@type": "Service", "name": "Travel Nursing" },
              { "@type": "Service", "name": "Allied Health PRN" },
              { "@type": "Service", "name": "Therapy PRN (PT, OT, SLP, RT)" }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.tritalcare.com/" },
              { "@type": "ListItem", "position": 2, "name": "ShiftBridge", "item": "https://www.tritalcare.com/shiftbridge" }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "How do I find PRN shifts near me?", "acceptedAnswer": { "@type": "Answer", "text": "Browse TRITAL ShiftBridge™ at /shiftbridge/browse — filter by state, specialty, shift type and minimum rate to find PRN shifts at vetted hospitals and SNFs in your area. Most clinicians find a same-week shift on day one." }},
              { "@type": "Question", "name": "What specialties have the most PRN openings?", "acceptedAnswer": { "@type": "Answer", "text": "ICU, ER, Med-Surg, Telemetry, L&D, OR, PACU, NICU/PICU, plus Therapy (PT/OT/SLP/RT) and Allied (CNA, MA, Surg Tech, Phlebotomy) are the most active categories on TRITAL ShiftBridge™." }},
              { "@type": "Question", "name": "Is TRITAL ShiftBridge a 1099 platform?", "acceptedAnswer": { "@type": "Answer", "text": "No. All TRITAL ShiftBridge™ assignments are W-2. Clinicians receive weekly pay with proper withholdings and employer-side payroll taxes — no 1099 surprises." }},
              { "@type": "Question", "name": "How quickly do PRN shifts fill?", "acceptedAnswer": { "@type": "Answer", "text": "Availability and booking timelines vary by facility, role, location and onboarding status." }}
            ]
          }
        ]}
      />

      {/* 1 · HERO ------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="shifts-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">PRN Marketplace · Browse by specialty and state</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[68px] leading-[0.98] tracking-tight text-slate-900 text-balance">
              Open shifts.<br />
              <span className="text-trital-navy">Real rates.</span>
              <span className="block text-trital-red">Vetted clinicians.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-[58ch] leading-relaxed text-lg">
              TRITAL ShiftBridge™ is the W-2 PRN marketplace built and operated by TRITAL Care® — the workforce partner trusted by hospitals from Houston to the Hudson. <span className="font-semibold text-slate-900">Zero clinician fees. Weekly pay. No 1099 surprises.</span>
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={SHIFTS_CTAS.clinicianSignup}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep shadow-[0_8px_30px_-8px_rgba(220,38,38,0.5)]"
                data-testid="hero-clinician-cta"
              >
                Find shifts as a clinician <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to={SHIFTS_CTAS.facilitySignup}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="hero-facility-cta"
              >
                Post shifts as a facility
              </Link>
            </div>

            {/* PRN search by city/specialty discoverability hint */}
            <div className="mt-6">
              <Link
                to="/shiftbridge/prn-shifts"
                className="inline-flex items-center gap-1.5 text-trital-navy text-[13px] font-semibold underline underline-offset-4 decoration-trital-navy/30 hover:decoration-trital-red hover:text-trital-red transition-colors"
                data-testid="hero-seo-hub-link"
              >
                Browse PRN shifts by city or specialty (RN, ICU, ER, Med-Surg…) <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>

            {/* Stats strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {num: loading ? "…" : shifts.length.toLocaleString("en-US"), label: "Recent open shifts returned"},
                {num: "RN", label: "Nursing shifts"},
                {num: "CNA", label: "Nursing support"},
                {num: "Allied", label: "Specialty workforce"}
              ].map((item,i) => (
                <div key={i} className="rounded-2xl bg-white/70 backdrop-blur border border-slate-200 px-4 py-3" data-testid={`hero-stat-${i}`}>
                  <div className="font-display font-black text-2xl text-trital-navy tabular-nums">{item.num}</div>
                  <div className="text-[11px] text-slate-600 leading-tight mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — featured shift cards stack */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-trital-medical-blue-soft via-transparent to-transparent rounded-[44px] blur-2xl opacity-70" />
            <div className="relative space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trital-navy text-white text-[10px] font-bold uppercase tracking-wider">
                <Zap className="w-3 h-3" strokeWidth={2} /> {availabilityError ? "Availability status" : "Live shifts"}
              </div>
              {loading ? (
                <>
                  <ShiftCardSkeleton index={0} />
                  <ShiftCardSkeleton index={1} />
                </>
              ) : shifts.length > 0 ? (
                shifts.slice(0, 2).map((s, i) => (
                  <ShiftCard key={s.id} shift={s} variant={i === 0 ? "feature" : "list"} index={i} />
                ))
              ) : (
                /* No fabricated job, facility or pay rate when the API returns no shifts. */
                <div className="rounded-3xl bg-white border border-slate-200 shadow-navy p-7" data-testid="hero-shift-fallback">
                  <div className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-trital-navy text-[10px] font-bold uppercase">Shift availability</div>
                  <h2 className="mt-3 font-display font-black text-2xl text-slate-900">{availabilityError ? "Shift availability temporarily unavailable" : "No published shifts available right now"}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid={availabilityError ? "shift-api-unavailable" : "shift-inventory-empty"}>
                    {availabilityError ? "The shift service is not connected or could not be reached. This does not mean there are no shifts. We will show verified, published shifts when connectivity is restored." : "No shifts have been returned by the connected service. Facilities can publish new opportunities after onboarding and approval."}
                  </p>
                  <Link to="/shiftbridge/signup/clinician" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-trital-navy text-white font-semibold text-sm">
                    Create clinician profile <ArrowRight className="w-4 h-4"/>
                  </Link>
                </div>
              )}
              <Link
                to={SHIFTS_CTAS.browse}
                className="block text-center rounded-2xl border border-slate-200 bg-white/80 backdrop-blur py-3.5 text-[13px] font-semibold text-trital-navy hover:bg-white hover:border-trital-navy"
                data-testid="hero-browse-all"
              >
                Browse all open shifts <ArrowUpRight className="inline w-3.5 h-3.5 ml-1 text-trital-red" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · HOW IT WORKS (clinician) ----------------------------- */}
      <HowItWorks
        eyebrow="How it works · for clinicians"
        title="From registration to a shift-ready profile."
        steps={HOW_IT_WORKS.clinician}
        cta={{ label: "Sign up as a clinician", to: SHIFTS_CTAS.clinicianSignup }}
        testid="hiw-clinician"
      />

      {/* 3 · FOR FACILITIES band ---------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="home-for-facilities">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <div className="overline text-trital-red">For facilities</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
              92% fill rate. One clean invoice. Zero gig-economy risk.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-[58ch]">
              Post a shift in 90 seconds. We fan it out to credential-matched clinicians and clear them through TRITAL Care's compliance pipeline before they ever scrub in.
            </p>
            <ul className="mt-6 space-y-3 text-[14px] text-slate-700">
              {[
                "Bill-rate transparency — no surprise margins",
                "Net-30 consolidated invoicing across all units",
                "Unit-level credentialing rules enforced at claim",
                "W-2 only — TRITAL Care carries workers' comp + tax",
              ].map((b) => (
                <li key={b} className="inline-flex items-start gap-2"><BadgeCheck className="w-4 h-4 mt-0.5 text-trital-red shrink-0" strokeWidth={2} />{b}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={SHIFTS_CTAS.facilitySignup}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-[14px] font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="for-facilities-cta"
              >
                Create facility account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/shifts/facilities"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full border border-slate-300 text-slate-700 text-[13.5px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-white"
              >
                Why TRITAL Care for facilities
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl bg-trital-navy-ink text-white p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-trital-navy-mesh opacity-80 pointer-events-none" />
              <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
              <div className="relative">
                <Building2 className="w-7 h-7 text-trital-red" strokeWidth={1.8} />
                <h3 className="mt-4 font-display font-black text-2xl text-balance leading-tight">A typical Tuesday on TRITAL ShiftBridge™</h3>
                <ul className="mt-5 space-y-4 text-[13.5px] text-white/80">
                  <li className="flex items-start gap-3">
                    <span className="text-trital-red font-mono font-bold pt-0.5">06:42</span>
                    <span>NICU 7-7p posts at $76/hr · 2 matched clinicians notified</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-trital-red font-mono font-bold pt-0.5">06:58</span>
                    <span>Shift claimed by traveler with active TX RN, BLS, NRP — auto-approved</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-trital-red font-mono font-bold pt-0.5">19:00</span>
                    <span>Clock-in confirmed. House supervisor pings TRITAL Care recruiter desk in app.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-trital-red font-mono font-bold pt-0.5">07:14</span>
                    <span>Shift complete. Time-keeping auto-syncs to facility scheduler. Invoice line booked.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 · FOR CLINICIANS band ---------------------------------- */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center" data-testid="home-for-clinicians">
        <div className="order-2 lg:order-1">
          <div className="rounded-3xl bg-trital-mesh border border-slate-200 p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
            <div className="relative">
              <HeartPulse className="w-7 h-7 text-trital-red" strokeWidth={1.8} />
              <h3 className="mt-4 font-display font-black text-2xl text-balance leading-tight text-slate-900">What you actually take home</h3>
              <div className="mt-5 grid grid-cols-2 gap-3 text-[13px]">
                {[
                  ["Posted rate",       "$74.00 / hr"],
                  ["12-hour shift",     "$888.00 gross"],
                  ["OT (>8h)",          "× 1.5 on hours 9–12"],
                  ["W-2 weekly direct deposit", "Friday by 9 AM"],
                  ["Clinician fees",    "$0 — always"],
                  ["Hidden 1099?",      "Never"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-2xl bg-white border border-slate-200 p-4">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">{k}</div>
                    <div className="mt-1 font-display font-black text-trital-navy text-[16px]">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <div className="overline text-trital-red">For clinicians</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            The pay you posted. The week you wanted. None of the gig-economy nonsense.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-[58ch]">
            Build a PRN week around your life — claim shifts as a W-2 employee with full workers' comp, weekly direct deposit, and rate transparency on every single posting.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={SHIFTS_CTAS.clinicianSignup}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="for-clinicians-cta"
            >
              Create clinician account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link
              to="/shifts/clinicians"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full border border-slate-300 text-slate-700 text-[13.5px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-slate-50"
            >
              How clinician pay works
            </Link>
          </div>
        </div>
      </section>

      {/* 5 · SHIFT CATEGORIES ------------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="home-categories">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="overline text-trital-red">Specialties on TRITAL ShiftBridge™</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
              Every clinical discipline. Every state. One platform.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SHIFT_CATEGORIES.map((c, i) => {
              const Icon = ICONS[c.icon] || HeartPulse;
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className="group relative rounded-3xl bg-white border border-slate-200 shadow-card p-6 hover:-translate-y-1 hover:shadow-card-hover transition-all"
                  data-testid={`category-${c.slug}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="mt-5 font-display font-bold text-[18px] text-slate-900">{c.label}</div>
                  <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{c.blurb}</p>
                  <Link
                    to={`/shifts/browse?specialty=${encodeURIComponent(c.slug)}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-trital-navy group-hover:gap-2 transition-all"
                  >
                    See open shifts <ArrowUpRight className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 · TRUST / COMPLIANCE ----------------------------------- */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="home-trust">
        <div className="max-w-3xl mx-auto text-center">
          <div className="overline text-trital-red">Trust & compliance</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            A marketplace built on Joint Commission rigor.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            TRITAL ShiftBridge™ inherits the full TRITAL Care® compliance posture. Clinicians must satisfy the applicable facility onboarding and credential requirements before being approved for an assignment.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST_PILLARS.map((p, i) => {
            const Icon = ICONS[p.icon] || ShieldCheck;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="rounded-3xl bg-white border border-slate-200 shadow-card p-6"
                data-testid={`trust-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={1.8} /></div>
                <div className="mt-5 font-display font-bold text-[16.5px] text-slate-900">{p.title}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{p.blurb}</p>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <Link to="/compliance" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-trital-navy hover:gap-2 transition-all" data-testid="home-compliance-link">
            Read the full compliance framework <ArrowUpRight className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
          </Link>
        </div>
      </section>

      {/* 7 · CTA BAND --------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-navy-ink text-white" data-testid="home-cta-band">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="overline text-trital-red">Ready when you are</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-[52px] tracking-tight leading-[1.02] text-balance">
              Healthcare workforce, on demand.
            </h2>
            <p className="mt-5 text-white/75 max-w-[58ch] leading-relaxed">
              Whether you're a charge nurse looking for an extra shift this Friday, or a CNO trying to fill a NICU 7-7p tonight — TRITAL ShiftBridge™ is the front door.
            </p>
          </div>
          <div className="space-y-3">
            <Link
              to={SHIFTS_CTAS.clinicianSignup}
              className="flex items-center justify-between gap-3 rounded-3xl bg-trital-red hover:bg-trital-red-deep px-6 py-5 transition-colors"
              data-testid="cta-band-clinician"
            >
              <div>
                <div className="font-display font-black text-xl">I'm a clinician</div>
                <div className="text-[12.5px] text-white/80 mt-0.5">Find shifts in 60 seconds</div>
              </div>
              <ArrowRight className="w-5 h-5" strokeWidth={1.8} />
            </Link>
            <Link
              to={SHIFTS_CTAS.facilitySignup}
              className="flex items-center justify-between gap-3 rounded-3xl bg-white text-trital-navy hover:bg-slate-100 px-6 py-5 transition-colors"
              data-testid="cta-band-facility"
            >
              <div>
                <div className="font-display font-black text-xl">I'm a facility</div>
                <div className="text-[12.5px] text-slate-600 mt-0.5">Post your first shift today</div>
              </div>
              <ArrowRight className="w-5 h-5 text-trital-red" strokeWidth={1.8} />
            </Link>
            <Link
              to={SHIFTS_CTAS.login}
              className="flex items-center justify-center gap-1.5 text-[12.5px] text-white/65 hover:text-white py-2"
              data-testid="cta-band-signin"
            >
              Already have an account? Sign in <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </ShiftsLayout>
  );
}
